import { type UIMatch } from 'react-router'

export const STUDY_HOME_PATH = '/studiu'
export const PROGRESS_HOME_PATH = '/studiu/progres'
export const LEGACY_PROGRESS_HOME_PATH = '/progress'
export const STUDY_REVIEW_PATH = '/studiu/recapitulare'
export const PROFILE_HOME_PATH = '/profil'
export const PROFILE_SECURITY_PATH = '/profil/securitate'
export const PROFILE_DATA_PATH = '/profil/date'
export const ONBOARDING_DIAGNOSTIC_PATH = '/onboarding/diagnostic'
export const PROFILE_PHOTO_PATH = '/profil/foto'
export const PROFILE_EMAIL_PATH = '/profil/email'
export const PROFILE_PASSWORD_PATH = '/profil/parola'
export const PROFILE_PASSWORD_CREATE_PATH = '/profil/parola/creeaza'
export const PROFILE_PASSKEYS_PATH = '/profil/chei-de-acces'
export const PROFILE_TWO_FACTOR_PATH = '/profil/securitate/2fa'
export const PROFILE_TWO_FACTOR_VERIFY_PATH =
	'/profil/securitate/2fa/verificare'
export const PROFILE_TWO_FACTOR_DISABLE_PATH =
	'/profil/securitate/2fa/dezactiveaza'

export type SectionCode = 'L0' | 'S1' | 'S2' | 'S3'
export type SectionPathSegment =
	| 'fundamente'
	| 'subiectul-i'
	| 'subiectul-ii'
	| 'subiectul-iii'
export type AppNavContext = 'study' | 'profile'
// One shared content frame for the whole app; 'lesson' keeps the same width
// but switches the chrome (navbar title, no context header, room for the
// sticky flow bar at the bottom).
export type AppPageWidth = 'content' | 'lesson'

export type AppNavCrumb = {
	label: string
	to?: string
}

export type AppNavItem = {
	context?: AppNavContext
	crumb?: AppNavCrumb | null
	pageWidth?: AppPageWidth
	backTo?: string | null
}

export type AppNavHandle =
	| {
			appNav?: AppNavItem | ((match: UIMatch) => AppNavItem | null | undefined)
	  }
	| undefined

const sectionSegmentByCode: Record<SectionCode, SectionPathSegment> = {
	L0: 'fundamente',
	S1: 'subiectul-i',
	S2: 'subiectul-ii',
	S3: 'subiectul-iii',
}

const sectionCodeBySegment: Record<SectionPathSegment, SectionCode> = {
	fundamente: 'L0',
	'subiectul-i': 'S1',
	'subiectul-ii': 'S2',
	'subiectul-iii': 'S3',
}

export function getSectionPathSegmentFromCode(
	sectionCode: SectionCode,
): SectionPathSegment {
	return sectionSegmentByCode[sectionCode]
}

export function getSectionCodeFromSegment(
	sectionSegment: string | undefined,
): SectionCode | null {
	if (!sectionSegment) return null
	return sectionCodeBySegment[sectionSegment as SectionPathSegment] ?? null
}

export function buildStudyProgressPath(sectionCode?: SectionCode | null) {
	if (!sectionCode) return PROGRESS_HOME_PATH

	const params = new URLSearchParams({
		section: getSectionPathSegmentFromCode(sectionCode),
	})
	return `${PROGRESS_HOME_PATH}?${params.toString()}`
}

export function buildStudySectionPath(sectionCode: SectionCode) {
	return `${STUDY_HOME_PATH}/${getSectionPathSegmentFromCode(sectionCode)}`
}

export function buildStudyLessonPath(
	sectionCode: SectionCode,
	areaId: string,
	lessonId: string,
) {
	return `${buildStudySectionPath(sectionCode)}/${encodeURIComponent(areaId)}/${encodeURIComponent(lessonId)}`
}

export function buildStudyPracticePath(sectionCode: SectionCode) {
	return `${buildStudySectionPath(sectionCode)}/simulare`
}

export function buildOnboardingDiagnosticPath(redirectTo?: string | null) {
	if (!redirectTo) return ONBOARDING_DIAGNOSTIC_PATH

	const params = new URLSearchParams({ redirectTo })
	return `${ONBOARDING_DIAGNOSTIC_PATH}?${params.toString()}`
}

export function buildOnboardingDiagnosticSessionPath(
	redirectTo: string,
	options: { resetSession?: boolean } = {},
) {
	const params = new URLSearchParams({ redirectTo })
	if (options.resetSession) {
		params.set('resetSession', 'true')
	}
	return `${ONBOARDING_DIAGNOSTIC_PATH}/session?${params.toString()}`
}

export function resolveAppNavItem(match: UIMatch) {
	const handle = match.handle as AppNavHandle
	if (!handle?.appNav) return null
	return typeof handle.appNav === 'function'
		? handle.appNav(match)
		: handle.appNav
}

// Deprecated aliases kept for compatibility while callers migrate to progress naming.
export const MAP_HOME_PATH = PROGRESS_HOME_PATH
export const LEGACY_MAP_HOME_PATH = LEGACY_PROGRESS_HOME_PATH
